import { useMemo } from "react";
import {
  UserAccessLevel,
  AssistantSection as AssistantSections,
} from "V2.0/models/UserProfile";

export default useRoleAccessBasedAssistantSections;

function isOperator(userAccessLevel) {
  return userAccessLevel === UserAccessLevel.WHITELABELOPERATOR;
}

type Section = "Configure" | "Dashboard" | "Assistant Coach" | "Automated Testing";

type ConfiguraitonSection =
  | "Prompt"
  | "Tools"
  | "Settings"
  | "Knowledge Base"
  | "Phone Numbers"
  | "Facebook Pages"
  | "Instagram Pages"
  | "Email Address"
  | "Widget";

const sectionsOrder: Record<Section, number> = {
  Configure: 0,
  Dashboard: 1,
  "Assistant Coach": 2,
  "Automated Testing": 3,
};

const configurationSectionsOrder: Record<ConfiguraitonSection, number> = {
  Prompt: 0,
  Tools: 1,
  Settings: 2,
  "Knowledge Base": 3,
  "Phone Numbers": 4,
  "Facebook Pages": 4,
  "Instagram Pages": 4,
  "Email Address": 4,
  Widget: 5,
};

type AssistantType =
  | "voice"
  | "sms"
  | "whatsapp"
  | "email"
  | "chat"
  | "messenger"
  | "instagram";

type VisibilityConfig = {
  showPrompt: boolean;
  showAssistantCoach: boolean;
  showAssistantCheckpointing: boolean;
};

type AssistantSection = {
  label: Section | ConfiguraitonSection;
  type: "Section" | "ConfigurationSection";
  assistant: Record<AssistantType, boolean>;
  visibilityProperties: Array<keyof VisibilityConfig>;
};

const assistantSections: Record<AssistantSections, AssistantSection> = {
  prompt: {
    label: "Prompt",
    type: "ConfigurationSection",
    visibilityProperties: ["showPrompt"],
    assistant: {
      voice: true,
      chat: true,
      email: true,
      messenger: true,
      instagram: true,
      whatsapp: true,
      sms: true,
    },
  },
  tools: {
    label: "Tools",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: true,
      email: true,
      messenger: true,
      instagram: true,
      whatsapp: true,
      sms: true,
    },
  },
  settings: {
    label: "Settings",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: false,
      email: false,
      messenger: true,
      instagram: true,
      whatsapp: true,
      sms: true,
    },
  },
  knowledgebase: {
    label: "Knowledge Base",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: true,
      email: true,
      messenger: true,
      instagram: true,
      whatsapp: true,
      sms: true,
    },
  },
  phoneNumbers: {
    label: "Phone Numbers",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: false,
      email: false,
      messenger: false,
      instagram: false,
      whatsapp: true,
      sms: true,
    },
  },
  widget: {
    label: "Widget",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: true,
      email: false,
      messenger: false,
      instagram: false,
      whatsapp: false,
      sms: false,
    },
  },
  dashboard: {
    label: "Dashboard",
    type: "Section",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: false,
      email: false,
      messenger: false,
      instagram: false,
      whatsapp: false,
      sms: false,
    },
  },
  assistantCoach: {
    label: "Assistant Coach",
    type: "Section",
    visibilityProperties: ["showAssistantCoach"],
    assistant: {
      voice: true,
      chat: false,
      email: false,
      messenger: false,
      instagram: false,
      whatsapp: false,
      sms: false,
    },
  },
  instagramPages: {
    label: "Instagram Pages",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: false,
      chat: false,
      email: false,
      messenger: false,
      instagram: true,
      whatsapp: false,
      sms: false,
    },
  },
  facebookPages: {
    label: "Facebook Pages",
    type: "ConfigurationSection",
    visibilityProperties: [],
    assistant: {
      voice: false,
      chat: false,
      email: false,
      messenger: true,
      instagram: false,
      whatsapp: false,
      sms: false,
    },
  },
  evalRuns: {
    label: "Automated Testing",
    type: "Section",
    visibilityProperties: [],
    assistant: {
      voice: true,
      chat: false,
      email: false,
      messenger: false,
      instagram: false,
      whatsapp: false,
      sms: false,
    },
  },
  // checkpoints: {
  //   label: "Checkpoints",
  //   type: "Section",
  //   visibilityProperties: ["showAssistantCheckpointing"],
  //   assistant: {
  //     voice: true,
  //     chat: true,
  //     sms: true,
  //     email: true,
  //     messenger: true,
  //     instagram: true,
  //     whatsapp: true,
  //   },
  // },
  // changelog: {
  //   label: "Changelogs",
  //   type: "Section",
  //   visibilityProperties: ["showAssistantCheckpointing"],
  //   assistant: {
  //     voice: true,
  //     chat: true,
  //     email: true,
  //     messenger: true,
  //     instagram: true,
  //     whatsapp: true,
  //     sms: true,
  //   },
  // },
};

function checkVisibility(visibilityProperties, visibilityConfig) {
  if (!visibilityProperties.length) {
    return true;
  }

  return visibilityProperties.reduce((acc, property) => {
    return acc && (visibilityConfig[property] ?? false);
  }, true);
}

function getVisibleAssistantSections(
  allowedSections,
  assistantType,
  visibilityConfig,
) {
  const { sections, configurationSections } = allowedSections.reduce(
    (acc, section) => {
      const assistantSection = assistantSections[section];

      if (!assistantSection) {
        return acc;
      }

      if (!assistantSection.assistant[assistantType]) {
        return acc;
      }

      const isVisible = checkVisibility(
        assistantSection.visibilityProperties,
        visibilityConfig,
      );

      if (!isVisible) {
        return acc;
      }

      switch (assistantSection.type) {
        case "Section":
          acc.sections.push(assistantSection.label);
          break;
        case "ConfigurationSection":
          acc.configurationSections.push(assistantSection.label);
          break;
      }

      return acc;
    },
    { sections: ["Configure"], configurationSections: [] },
  );

  return {
    sections: sections.sort((a, b) => sectionsOrder[a] - sectionsOrder[b]),
    configurationSections: configurationSections.sort(
      (a, b) => configurationSectionsOrder[a] - configurationSectionsOrder[b],
    ),
  };
}

function getDefaultAssistantSections(assistantType, visibilityConfig) {
  const { sections, configurationSections } = Object.values(
    assistantSections,
  ).reduce(
    (acc, section) => {
      if (!section.assistant[assistantType]) {
        return acc;
      }

      const isVisible = checkVisibility(
        section.visibilityProperties,
        visibilityConfig,
      );

      if (!isVisible) {
        return acc;
      }

      switch (section.type) {
        case "Section":
          acc.sections.push(section.label);
          break;
        case "ConfigurationSection":
          acc.configurationSections.push(section.label);
          break;
      }

      return acc;
    },
    { sections: ["Configure"], configurationSections: [] },
  );

  return {
    sections: sections.sort((a, b) => sectionsOrder[a] - sectionsOrder[b]),
    configurationSections: configurationSections.sort(
      (a, b) => configurationSectionsOrder[a] - configurationSectionsOrder[b],
    ),
  };
}

function useRoleAccessBasedAssistantSections(
  userProfile,
  assistantType,
  visibilityConfig,
) {
  const userAccessLevel = userProfile?.userAccessLevel ?? UserAccessLevel.WHITELABELVIEWER;

  const roleAccessBasedAssistantSections = useMemo(() => {
    if (!assistantType || !userProfile) {
      return { sections: ["Configure"], configurationSections: [] };
    }

    if (isOperator(userAccessLevel)) {
      return getVisibleAssistantSections(
        userProfile.assistantSectionsVisible,
        assistantType,
        visibilityConfig,
      );
    }

    return getDefaultAssistantSections(assistantType, visibilityConfig);
  }, [userProfile, assistantType, visibilityConfig]);

  return roleAccessBasedAssistantSections;
}
